import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { 
  TextField, 
  Button, 
  Container, 
  Typography, 
  Box, 
  Alert,
  Paper
} from '@mui/material';
import api from '../api/axios';

const ChangePasswordSchema = Yup.object().shape({
  currentPassword: Yup.string().required('Required'),
  newPassword: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/, 
      'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character')
    .required('Required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
    .required('Required'),
});

const ChangePasswordPage = () => {
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  return (
    <Container maxWidth="sm">
      <Box mt={8}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h5" align="center" gutterBottom>
            Change Password
          </Typography>
          <Typography variant="body2" align="center" color="textSecondary" sx={{ mb: 3 }}>
            Enter your current password and choose a new password.
          </Typography>
          
          <Formik
            initialValues={{ 
              currentPassword: '', 
              newPassword: '', 
              confirmPassword: '' 
            }}
            validationSchema={ChangePasswordSchema}
            onSubmit={async (values, { setSubmitting, resetForm }) => {
              setError('');
              setMessage('');
              try {
                const res = await api.post('/auth/change-password', {
                  currentPassword: values.currentPassword,
                  newPassword: values.newPassword
                });
                setMessage(res.data.message);
                resetForm();
              } catch (err) {
                setError(err.response?.data?.message || 'An error occurred');
              }
              setSubmitting(false);
            }}
          >
            {({ isSubmitting, errors, touched }) => (
              <Form>
                <Field
                  as={TextField}
                  name="currentPassword"
                  label="Current Password"
                  type="password"
                  fullWidth
                  margin="normal"
                  error={touched.currentPassword && !!errors.currentPassword}
                  helperText={touched.currentPassword && errors.currentPassword}
                />
                <Field
                  as={TextField}
                  name="newPassword"
                  label="New Password"
                  type="password"
                  fullWidth
                  margin="normal"
                  error={touched.newPassword && !!errors.newPassword}
                  helperText={touched.newPassword && errors.newPassword}
                />
                <Field
                  as={TextField}
                  name="confirmPassword"
                  label="Confirm New Password"
                  type="password"
                  fullWidth
                  margin="normal"
                  error={touched.confirmPassword && !!errors.confirmPassword}
                  helperText={touched.confirmPassword && errors.confirmPassword}
                />
                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                {message && <Alert severity="success" sx={{ mt: 2 }}>{message}</Alert>}
                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                    sx={{ flex: 1 }}
                  >
                    Change Password
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/')}
                    sx={{ flex: 1 }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        </Paper>
      </Box>
    </Container>
  );
};

export default ChangePasswordPage; 